import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { fetchWecomDepartments, fetchWecomDeptUsers, inviteWecomUsers } from '@/api/wecomInvite';
import type { WecomDepartment, WecomDeptUser, WecomInviteResult } from '@/api/wecomInvite';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/feature/Layout';

export default function WecomInvitePage() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === '系统管理员';

  const [departments, setDepartments] = useState<WecomDepartment[]>([]);
  const [activeDeptId, setActiveDeptId] = useState<number | null>(null);
  const [users, setUsers] = useState<WecomDeptUser[]>([]);
  const [selected, setSelected] = useState<Record<string, string>>({}); // wecomUserId -> name
  const [loading, setLoading] = useState(false);
  const [deptLoading, setDeptLoading] = useState(true);
  const [result, setResult] = useState<WecomInviteResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    setDeptLoading(true);
    fetchWecomDepartments()
      .then(setDepartments)
      .catch((e) => setError(e?.message || '企微通讯录拉取失败'))
      .finally(() => setDeptLoading(false));
  }, [isAdmin]);

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const openDept = (deptId: number) => {
    setActiveDeptId(deptId);
    setLoading(true);
    setError('');
    fetchWecomDeptUsers(deptId)
      .then(setUsers)
      .catch((e) => setError(e?.message || '成员列表拉取失败'))
      .finally(() => setLoading(false));
  };

  const toggle = (u: WecomDeptUser) => {
    if (u.alreadyJoined) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[u.userId]) delete next[u.userId];
      else next[u.userId] = u.name;
      return next;
    });
  };

  const submitInvite = () => {
    const items = Object.entries(selected).map(([wecomUserId, name]) => ({ wecomUserId, name }));
    if (items.length === 0) return;
    setLoading(true);
    setError('');
    inviteWecomUsers(items)
      .then((r) => {
        setResult(r);
        setSelected({});
        if (activeDeptId != null) openDept(activeDeptId);
      })
      .catch((e) => setError(e?.message || '邀请提交失败'))
      .finally(() => setLoading(false));
  };

  const selectedCount = Object.keys(selected).length;

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5">
        {/* Page header */}
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">企微邀请建号</h1>
          <p className="text-sm text-foreground-500 mt-1">从企业微信通讯录选择成员，批量邀请注册为系统账号</p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Result banner */}
        {result && (
          <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-3">
            <p className="text-sm text-primary-700">
              成功邀请 <strong>{result.invited.length}</strong> 人，跳过(已加入) <strong>{result.skipped.length}</strong> 人
              {result.failed.length > 0 && (
                <>
                  ，失败 <strong>{result.failed.length}</strong> 人
                </>
              )}
            </p>
            {result.failed.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {result.failed.map((f) => (
                  <li key={f.wecomUserId} className="text-xs text-red-600">
                    {f.wecomUserId}: {f.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Department tree */}
          <div className="w-full lg:w-64 flex-shrink-0 bg-background-50 rounded-xl border border-background-200/70 p-4">
            <h3 className="text-sm font-heading font-semibold text-foreground-800 mb-3">部门</h3>
            {deptLoading ? (
              <div className="flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-loader-4-line animate-spin"></i>
                加载中...
              </div>
            ) : (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {departments.map((d) => (
                  <div
                    key={d.id}
                    onClick={() => openDept(d.id)}
                    className={`cursor-pointer px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeDeptId === d.id
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-800'
                    }`}
                  >
                    {d.name}
                  </div>
                ))}
                {departments.length === 0 && (
                  <p className="text-xs text-foreground-400 px-1">暂无部门数据</p>
                )}
              </div>
            )}
          </div>

          {/* Member list */}
          <div className="flex-1 bg-background-50 rounded-xl border border-background-200/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-heading font-semibold text-foreground-800">成员</h3>
              <button
                onClick={submitInvite}
                disabled={loading || selectedCount === 0}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-user-add-line text-base"></i>
                邀请选中成员({selectedCount})
              </button>
            </div>

            {activeDeptId === null ? (
              <div className="py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-team-line text-xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">请先在左侧选择部门</p>
                </div>
              </div>
            ) : loading ? (
              <div className="py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
                  <p className="text-sm text-foreground-500">正在加载成员...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {users.map((u) => (
                  <label
                    key={u.userId}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      u.alreadyJoined ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-background-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={u.alreadyJoined}
                      checked={!!selected[u.userId]}
                      onChange={() => toggle(u)}
                      className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <span className="text-sm text-foreground-800">{u.name}</span>
                    {u.alreadyJoined && (
                      <span className="text-xs text-foreground-400">(已加入)</span>
                    )}
                  </label>
                ))}
                {users.length === 0 && (
                  <p className="text-sm text-foreground-400 text-center py-8">该部门暂无成员</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
